/**
 * Hero Gallery Slideshow
 * Touch-enabled slideshow for hero gallery section
 */

if (!customElements.get('hero-gallery-slideshow')) {
  class HeroGallerySlideshow extends HTMLElement {
    constructor() {
      super();
      this.currentSlide = 0;
      this.touchStartX = 0;
      this.touchEndX = 0;
      this.touchStartY = 0;
      this.touchEndY = 0;
      this.swipeThreshold = 50;
    }

    connectedCallback() {
      this.slides = this.querySelectorAll('.slide');
      this.dots = this.querySelectorAll('.gallery-dot'); // Scoped to this component only

      if (this.slides.length === 0) return;

      this.initializeTouchEvents();
      this.initializeButtons();
      this.updateAriaAttributes();
    }

    updateSlide(newIndex) {
      this.slides[this.currentSlide].classList.remove('active');
      if (this.dots[this.currentSlide]) {
        this.dots[this.currentSlide].classList.remove('active');
        this.dots[this.currentSlide].removeAttribute('aria-current');
      }

      this.currentSlide = newIndex;

      this.slides[this.currentSlide].classList.add('active');
      if (this.dots[this.currentSlide]) {
        this.dots[this.currentSlide].classList.add('active');
        this.dots[this.currentSlide].setAttribute('aria-current', 'true');
      }
    }

    nextSlide() {
      const newIndex = (this.currentSlide + 1) % this.slides.length;
      this.updateSlide(newIndex);
    }

    prevSlide() {
      const newIndex = (this.currentSlide - 1 + this.slides.length) % this.slides.length;
      this.updateSlide(newIndex);
    }

    goToSlide(index) {
      this.updateSlide(index);
    }

    initializeTouchEvents() {
      this.addEventListener('touchstart', (e) => {
        this.touchStartX = e.changedTouches[0].screenX;
        this.touchStartY = e.changedTouches[0].screenY;
      }, { passive: true });

      this.addEventListener('touchend', (e) => {
        this.touchEndX = e.changedTouches[0].screenX;
        this.touchEndY = e.changedTouches[0].screenY;
        this.handleSwipe();
      }, { passive: true });
    }

    handleSwipe() {
      const diffX = this.touchStartX - this.touchEndX;
      const diffY = this.touchStartY - this.touchEndY;

      // Only trigger if horizontal swipe is stronger than vertical
      if (Math.abs(diffX) > Math.abs(diffY)) {
        if (Math.abs(diffX) > this.swipeThreshold) {
          if (diffX > 0) {
            // Swiped left - next slide
            this.nextSlide();
          } else {
            // Swiped right - previous slide
            this.prevSlide();
          }
        }
      }
    }

    // Initialize button event listeners (replaces onclick handlers)
    initializeButtons() {
      const prevBtn = this.querySelector('[data-action="prev-slide"]');
      const nextBtn = this.querySelector('[data-action="next-slide"]');
      const dotBtns = this.querySelectorAll('[data-slide-index]');

      if (prevBtn) {
        prevBtn.addEventListener('click', () => this.prevSlide());
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', () => this.nextSlide());
      }

      dotBtns.forEach(dot => {
        dot.addEventListener('click', () => {
          const index = parseInt(dot.dataset.slideIndex, 10);
          this.goToSlide(index);
        });
      });
    }

    // Update ARIA attributes for accessibility
    updateAriaAttributes() {
      this.slides.forEach((slide, index) => {
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-roledescription', 'slide');
        slide.setAttribute('aria-label', `Slide ${index + 1} of ${this.slides.length}`);
      });
    }
  }

  customElements.define('hero-gallery-slideshow', HeroGallerySlideshow);
}
